import { TextField } from "@material-ui/core";
import { FC, useEffect, useRef, useState } from "react";
import {
  Edit,
  EditProps,
  email,
  FormGroupContextProvider,
  FormWithRedirect,
  minLength,
  PasswordInput,
  required,
  SelectInput,
  TextInput,
  useLoading,
} from "react-admin";

import CustomCard from "../../components/CustomCard";
import CustomToolbar from "../../components/CustomToolbar";
import FormWrapper from "../../components/FormWrapper";
import ImageUploadInput from "../../components/ImageUploadInput";
import Spacing from "../../components/Spacing";
import useDisabledOnLoading from "../../hooks/useDisabledOnLoading";
import useGetEnumsAsChoices from "../../hooks/useGetEnumsAsChoices";
import firebaseConfig from "../../init/config";
import { firestore } from "../../init/firebase";
import { FormGroupNames } from "../../types/reactAdmin.types";
import { AppResource, Enums } from "../../types/resource.types";
import { isExists, validateFileSize } from "../../utils/validators";
import useBrandsLocalizations from "./utils/useBrandsLocalizations";

const CustomForm: FC<CustomEditProps> = (props) => {
  const loading = useLoading();
  const { emailRef } = props;

  const { disabledStyles } = useDisabledOnLoading(loading);
  const localizations = useBrandsLocalizations(props);
  const brandsStatus = useGetEnumsAsChoices<Enums>(Enums.status);

  const BrandsQuery = (key: string, value: string) =>
    firestore.collection(AppResource.brands).where(key, "==", value).limit(1);

  const [emailError, setEmailError] = useState<boolean | string>("");
  const [enteredEmail, setEnteredEmail] = useState(props.record.email);

  const emailHandler = async (e: any) => {
    const value = e.target.value;
    setEnteredEmail(value);
    if (value.trim() && value === props.record.email) {
      setEmailError("");
      emailRef.current = props.record.email;
    } else if (value.trim()) {
      const regex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
      if (regex.test(value)) {
        const urlencoded = new URLSearchParams();
        urlencoded.append("email", value);
        const response = await fetch(`${firebaseConfig.cloudFunctionsBaseUrl}/functions-checkingAuth`, {
          method: "post",
          body: urlencoded,
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        });
        if (response.status === 200) {
          emailRef.current = value;
          setEmailError("");
        } else {
          setEmailError("Email already exists");
        }
      } else {
        setEmailError("Invalid email");
      }
    } else {
      setEmailError(true);
    }
  };

  useEffect(() => {
    emailRef.current = props.record.email;
  }, []);

  return (
    <FormWrapper>
      <FormGroupContextProvider name={FormGroupNames.brandsEdit}>
        <FormWithRedirect
          {...props}
          redirect={`/${AppResource.brands}`}
          render={(formProps) => {
            return (
              <form style={disabledStyles}>
                <CustomCard title="Details">
                  <Spacing>
                    {localizations?.[0]}
                    {localizations?.[1]}
                  </Spacing>

                  <Spacing>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      onChange={emailHandler}
                      style={{ marginTop: 8 }}
                      label="Email"
                      helperText={enteredEmail.length && emailError ? emailError : ""}
                      error={!!enteredEmail && !!emailError}
                      defaultValue={enteredEmail}
                    />

                    <PasswordInput
                      source="password"
                      variant="outlined"
                      type="password"
                      fullWidth
                      validate={[minLength(8)]}
                    />
                  </Spacing>

                  <SelectInput
                    source="status"
                    choices={brandsStatus}
                    variant="outlined"
                    validate={[required()]}
                    fullWidth
                    label="Status"
                  />
                </CustomCard>

                <CustomCard title="Logo">
                  <ImageUploadInput source="logo" validate={[required(), validateFileSize("logo")]} />
                </CustomCard>

                <CustomToolbar
                  {...formProps}
                  formName={FormGroupNames.brandsEdit}
                  customErrors={{ email: (enteredEmail.length === 0 && emailError) || (!!enteredEmail && emailError) }}
                />
              </form>
            );
          }}
        />
      </FormGroupContextProvider>
    </FormWrapper>
  );
};

const BrandsEdit: FC<EditProps> = (props) => {
  const emailRef = useRef();
  return (
    <Edit
      {...props}
      component="section"
      transform={(values) => {
        values.email = emailRef.current;
        return values;
      }}
    >
      <CustomForm sanitizeEmptyValues={false} emailRef={emailRef} />
    </Edit>
  );
};

interface CustomEditProps {
  [key: string]: any;
}

export default BrandsEdit;
